import { Product } from '../services/orderLinkAPI';

export interface CartItem {
  product: Product;
  quantity: number;
  product_variables?: Record<string, any>;
  finalPrice?: number;
  // Merged item fields
  id?: string;
  isMerged?: boolean;
  corteType?: string;
  groupSize?: number;
  items?: CartItem[];
  mostExpensive?: CartItem;
  displayName?: string;
}

// Helper function to merge fractional pizzas (Meia=2, Terço=3, Quarto=4)
export const mergeFractionalPizzas = (items: CartItem[], cobrarPrecoMaior?: boolean): CartItem[] => {
  if (!cobrarPrecoMaior) return items;

  const fractionalSizes: Record<string, number> = {
    'Meia': 2,
    'Terço': 3,
    'Quarto': 4
  };

  // Group items by groupId + corteType
  const groups: Map<string, CartItem[]> = new Map();
  const otherItems: CartItem[] = [];

  // Separate fractional pizzas by groupId and cut type
  items.forEach(item => {
    const isPizza = item.product?.product_type?.type === 'Pizza';
    let corteRawValue = item.product_variables?.Corte;

    // Se corteValue é array, pegar o último valor (o selecionado)
    const corteValue = Array.isArray(corteRawValue)
      ? corteRawValue[corteRawValue.length - 1]
      : corteRawValue;

    const groupId = item.product_variables?._groupId;

    if (isPizza && corteValue && corteValue in fractionalSizes && groupId) {
      // Use groupId + corteType as key to group parts that belong together
      const key = `${groupId}_${corteValue}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    } else {
      otherItems.push(item);
    }
  });

  // Process each group
  const mergedItems: CartItem[] = [];

  groups.forEach((groupItems, key) => {
    // Extract corte type from key (format: "groupId_corteType")
    const corteType = key.split('_').pop()!;
    const groupSize = fractionalSizes[corteType];

    if (groupItems.length === groupSize) {
      // Complete group - merge into single item
      const prices = groupItems.map(item => ({
        item,
        price: item.finalPrice || item.product?.price || 0
      }));
      prices.sort((a, b) => b.price - a.price);
      const mostExpensive = prices[0].item;

      const displayName = groupItems.map(item =>
        `${corteType} ${item.product?.name}`
      ).join(' + ');

      // Generate unique ID for the merged item
      const mergedId = `merged_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      mergedItems.push({
        ...mostExpensive,
        id: mergedId,
        isMerged: true,
        corteType: corteType,
        groupSize: groupSize,
        items: groupItems,
        mostExpensive: mostExpensive,
        displayName: displayName,
        quantity: 1,
        finalPrice: Math.max(...groupItems.map(item => item.finalPrice || item.product?.price || 0))
      });
    } else {
      // Incomplete group - keep parts separate
      mergedItems.push(...groupItems);
    }
  });

  return [...mergedItems, ...otherItems];
};

// Helper function to calculate merged item price
export const calculateMergedItemPrice = (item: CartItem): number => {
  let basePrice = item.product?.price || 0;
  let finalPrice = basePrice;
  const multipliers: number[] = [];

  // Aplicar APENAS as customizações do item mais caro (ignorando Corte)
  if (item.product_variables && item.product?.attributes) {
    Object.entries(item.product_variables).forEach(([attributeName, selectedValue]) => {
      // Ignorar Corte
      if (attributeName === 'Corte') return;

      const attributeData = item.product.attributes[attributeName];
      if (!attributeData || typeof attributeData !== 'object') return;

      const processValue = (priceValue: string) => {
        if (priceValue.startsWith('x')) {
          // Multiplicador
          const multiplier = parseFloat(priceValue.substring(1));
          if (!isNaN(multiplier)) {
            multipliers.push(multiplier);
          }
        } else {
          // Adição/Subtração
          const extraPrice = parseFloat(priceValue);
          if (!isNaN(extraPrice)) {
            finalPrice += extraPrice;
          }
        }
      };

      if (Array.isArray(selectedValue)) {
        // Para checkbox (múltiplos valores)
        selectedValue.forEach((value: string) => {
          if (attributeData[value] && typeof attributeData[value] === 'string') {
            processValue(attributeData[value]);
          }
        });
      } else if (selectedValue && typeof selectedValue === 'string') {
        // Para dropdown (valor único)
        if (attributeData[selectedValue] && typeof attributeData[selectedValue] === 'string') {
          processValue(attributeData[selectedValue]);
        }
      }
    });
  }

  // Aplicar multiplicadores por último
  multipliers.forEach(multiplier => {
    finalPrice = finalPrice * multiplier;
  });

  return finalPrice;
};
